'use client';

import { FiClock } from 'react-icons/fi';
import ReactHtmlParser from 'react-html-parser';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineCpuChip, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { RiFlashlightLine, RiGlobalLine, RiSparkling2Line } from 'react-icons/ri';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import ChatbotsNewFeatures from '@/components/blocks/ChatbotsNewFeatures';
import BlockDescriptionImageRight from '@/components/blocks/BlockDescriptionImageRight';
import BlockDescriptionImageLeft from '@/components/blocks/BlockDescriptionImageLeft';
import { Comparisons } from '@/constants/Comparison';
import Image from 'next/image';
import LouiseButton, { CtaTargetType } from '@/components/buttons/LouiseButton';
import ReadyTransformCustomerExperience from '@/components/blocks/ReadyTransformCustomerExperience';
import SectionIntegration from '@/components/sectionIntegration/SectionIntegration';
import React from 'react';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import { HiOutlineDocumentText } from 'react-icons/hi';
import GeneralChatbotBenefits from '@/components/blocks/GeneralChatbotBenefits';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const sections = t('features/aiChatbot/WhyChooseAIBot/sections', { returnObjects: true }) as Array<any>;

  const hero = {
    content: {
      badge: t('features/aiChatbot/hero-badge'),
      title: t('features/aiChatbot/hero-title'),
      description: t('features/aiChatbot/hero-subtitle'),
      cta: t('features/aiChatbot/hero-cta'),
      btnDemoText: t('features/aiChatbot/watch-demo'),
      trustedBy: t('features/aiChatbot/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071515443446_ai-chatbot.png',
      icon: HiOutlineCpuChip,
      ctaTarget: 'register' as CtaTargetType,
      btnDemoTarget: 'discover-chatbot-ai' as WatchButtonCtaTargetType,
      imgOrientation: 'portrait' as IImageOrientation,
    },
    benefits: [
      { icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />, text: t('features/aiChatbot/hero-benefit1') },
      { icon: <FiClock className="tw-w-5 tw-h-5" />, text: t('features/aiChatbot/hero-benefit2') },
    ],
    stats: [
      { value: '5 Min', label: t('features/aiChatbot/focus1') },
      { value: '99.9%', label: t('features/aiChatbot/focus2') },
      { value: '100%', label: t('features/aiChatbot/focus3') },
    ],
  };

  const whyChooseThisBot = {
    section: {
      title: t('features/aiChatbot/WhyChooseTextBot/title'),
      subtitle: t('features/aiChatbot/WhyChooseTextBot/subtitle'),
      description: t('features/aiChatbot/WhyChooseTextBot/description'),
    },
    argument: [
      {
        iconicText: t(sections[0].improvement),
        title: t(sections[0].title),
        description: t(sections[0].description),
        outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
        metricLabel: t(sections[0].metricLabel),
        metric: t(sections[0].metric),
        colorFrom: 'pink' as ColorDefaultChoice,
        colorTo: 'purple' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[1].improvement),
        title: t(sections[1].title),
        description: t(sections[1].description),
        outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
        metricLabel: t(sections[1].metricLabel),
        metric: t(sections[1].metric),
        colorFrom: 'blue' as ColorDefaultChoice,
        colorTo: 'cyan' as ColorDefaultChoice,
      },
      {
        iconicText: t(sections[2].improvement),
        title: t(sections[2].title),
        description: t(sections[2].description),
        outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
        metricLabel: t(sections[2].metricLabel),
        metric: t(sections[2].metric),
        colorFrom: 'green' as ColorDefaultChoice,
        colorTo: 'emerald' as ColorDefaultChoice,
      },
    ],
  };

  const footerSectionBlock = t('features/aiChatbot/NewFeaturesForAIChatbots/footerSectionBlock', {
    returnObjects: true,
  }) as Array<any>;

  const newFeatures = {
    section: {
      badge: t('features/aiChatbot/NewFeaturesForAIChatbots/badge'),
      title: t('features/aiChatbot/NewFeaturesForAIChatbots/title'),
      description: t('features/aiChatbot/NewFeaturesForAIChatbots/description'),
      subtitle: t('features/aiChatbot/NewFeaturesForAIChatbots/subtitle'),
      badgeCatchEye: {
        title: t('features/aiChatbot/NewFeaturesForAIChatbots/badgeCatchEye/title'),
        description: t('features/aiChatbot/NewFeaturesForAIChatbots/badgeCatchEye/description'),
      },
      footerSectionBlock: footerSectionBlock,
    },
    features: [
      {
        title: t('features/aiChatbot/NewFeaturesForAIChatbots/features/quickbot/title'),
        description: t('features/aiChatbot/NewFeaturesForAIChatbots/features/quickbot/description'),
        subtitle: t('features/aiChatbot/NewFeaturesForAIChatbots/features/quickbot/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071015310467_fast.png',
        icon: <RiFlashlightLine className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/aiChatbot/NewFeaturesForAIChatbots/features/docbot/title'),
        description: t('features/aiChatbot/NewFeaturesForAIChatbots/features/docbot/description'),
        subtitle: t('features/aiChatbot/NewFeaturesForAIChatbots/features/docbot/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/2507101531048_knowledge.png',
        icon: <HiOutlineDocumentText className="tw-w-6 tw-h-6 tw-text-white" />,
      },
      {
        title: t('features/aiChatbot/NewFeaturesForAIChatbots/features/sitebot/title'),
        description: t('features/aiChatbot/NewFeaturesForAIChatbots/features/sitebot/description'),
        subtitle: t('features/aiChatbot/NewFeaturesForAIChatbots/features/sitebot/subtitle'),
        img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071015310448_sitebot.png',
        icon: <RiGlobalLine className="tw-w-6 tw-h-6 tw-text-white" />,
      },
    ],
  };

  return (
    <div className="tw-relative tw-overflow-hidden">
      <div className="tw-relative tw-overflow-hidden">
        {/* Hero Section - Full Width */}
        <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

        <AnimatedGradientWrapper>
          <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <WhyChooseThisBot
              title={whyChooseThisBot.section.title}
              subtitle={whyChooseThisBot.section.subtitle}
              description={whyChooseThisBot.section.description}
              sections={whyChooseThisBot.argument}
            />

            <ChatbotsNewFeatures
              features={newFeatures.features}
              subtitle={newFeatures.section.subtitle}
              description={newFeatures.section.description}
              title={newFeatures.section.title}
              badge={newFeatures.section.badge}
              badgeCatchEye={newFeatures.section.badgeCatchEye}
              footerSectionBlock={newFeatures.section.footerSectionBlock}
              locale={locale as SupportedLanguages}
            />
          </section>

          {/* Feature Sections */}
          <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
              <div className="tw-space-y-10">
                <BlockDescriptionImageRight
                  badge={t('features/aiChatbot/simple-setup-badge')}
                  badgeIcon={<RiSparkling2Line className="tw-w-4 tw-h-4" />}
                  title={t('features/aiChatbot/bloc1/title')}
                  description={t('features/aiChatbot/bloc1/description')}
                  btnColor="blue"
                  cta={t('features/aiChatbot/bloc1/cta')}
                  image={'https://cdn.1cdn.app/application/LOUISEBOT/25071012064476_3steps_2.png'}
                  link="register"
                  locale={locale}
                />

                <BlockDescriptionImageLeft
                  title={t('features/aiChatbot/bloc2/title')}
                  description={t('features/aiChatbot/bloc2/description')}
                  btnColor="green"
                  cta={t('features/aiChatbot/bloc2/cta')}
                  image={
                    'https://cdn.1cdn.app/application/LOUISEBOT/24120308054235_page_ai_predefined_templates_for_quick_deployment.png'
                  }
                  link="register"
                  locale={locale}
                />

                {/* No Coding */}
                <BlockDescriptionImageRight
                  title={t('features/aiChatbot/bloc3/title')}
                  description={t('features/aiChatbot/bloc3/description')}
                  btnColor="blue"
                  cta={t('features/aiChatbot/bloc3/cta')}
                  image={'https://cdn.1cdn.app/application/LOUISEBOT/24120308054170_page_ai_no_coding_needed.png'}
                  link="register"
                  locale={locale}
                />

                <BlockDescriptionImageLeft
                  title={t('features/aiChatbot/bloc4/title')}
                  description={t('features/aiChatbot/bloc4/description')}
                  btnColor="green"
                  cta={t('features/aiChatbot/bloc4/cta')}
                  image={
                    'https://cdn.1cdn.app/application/LOUISEBOT/24120308054125_page_ai_knowledge_integration_made_easy.png'
                  }
                  link="register"
                  locale={locale}
                />
              </div>
            </div>
          </section>

          <GeneralChatbotBenefits locale={locale} />
          {/* Comparison Section */}
          <section className="tw-py-20">
            <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
              <div className="tw-text-center tw-mb-16">
                <h2 className="tw-text-3xl lg:tw-text-4xl xl:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
                  {t('features/aiChatbot/sectionOpponents/key1')}
                </h2>
                <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto">
                  {t('features/aiChatbot/comparison-description')}
                </p>
              </div>

              <div className="tw-grid sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-8">
                {Comparisons.map((item, index) => (
                  <div
                    key={item.name}
                    className="tw-group tw-bg-white tw-rounded-2xl tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-105 tw-border tw-border-gray-100"
                  >
                    <div className="tw-relative tw-overflow-hidden">
                      <Image
                        priority={index < 4}
                        quality={100}
                        src={item.imageUrl}
                        alt={t(item.name)}
                        width={300}
                        height={200}
                        className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                      />
                    </div>
                    <div className="tw-p-6">
                      <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-3 tw-leading-tight">
                        {t(item.name)}
                      </h3>
                      <p className="tw-text-gray-600 tw-text-sm tw-mb-4 tw-line-clamp-3 tw-leading-relaxed">
                        {ReactHtmlParser(t(item.summary_short))}
                      </p>
                      <LouiseButton
                        color="transparent"
                        cta={t('features/aiChatbot/sectionOpponents/key2')}
                        ctaTarget="other"
                        href={`/comparison/${item.pagePath}`}
                        locale={locale}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA - With background but content in container */}
          <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <ReadyTransformCustomerExperience locale={locale} />
          </section>

          <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <SectionIntegration locale={locale} />
          </section>

          <SiteFrequentlyAskedQuestionsComponent locale={locale} session="AIChatbotFAQs" />
        </AnimatedGradientWrapper>
      </div>
    </div>
  );
}
